import React, { useEffect, useRef } from "react";
import { Row, Col } from "antd";
import {
  ContainerPanel,
  ContainerModal,
  CompsTree,
  CompsForm,
} from "@ms/shared-components";
// Configs
import {
  createSpaceFormItems,
  getDefaultFormValues,
  getFormTitle,
  FORM_WIDTH,
  FORM_LAYOUT,
} from "./configs/formConfig";

// Hooks
import { useSpaceTreeData } from "./hooks/useSpaceTreeData";
import { useSpaceTreeOperations } from "./hooks/useSpaceTreeOperations";
import { useSpaceDetailPanel } from "./hooks/useSpaceDetailPanel";

import { treeMenuItems, createTreeButtons } from "./configs/treeConfig";

const SpaceTree: React.FC = () => {
  // 表单引用
  const formRef = useRef<any>(null);

  // 数据管理
  const {
    treeData,
    loading,
    expandedKeys,
    loadBuildings,
    handleNodeExpand,
    reloadTree,
    reloadNodeChildren,
  } = useSpaceTreeData();

  // 业务操作
  const {
    selectedSpace,
    editingSpace,
    formModalVisible,
    selectedKeys,
    setFormModalVisible,
    setEditingSpace,
    setSelectedKeys,
    setSelectedSpace,
    handleTreeSelect,
    handleCreate,
    handleMenuClick,
    handleFormSuccess,
    handleDetailPanelDelete,
  } = useSpaceTreeOperations(reloadTree, treeData, reloadNodeChildren);

  // 页面初始化时加载建筑数据
  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  // 当树数据加载完成且没有选中项时，自动选择第一条记录
  useEffect(() => {
    if (!loading && treeData.length > 0 && selectedKeys.length === 0 && !selectedSpace) {
      const firstNode = treeData[0];
      if (firstNode && firstNode.space) {
        setSelectedKeys([firstNode.key]);
        setSelectedSpace(firstNode.space);
      }
    }
  }, [loading, treeData, selectedKeys, selectedSpace, setSelectedKeys, setSelectedSpace]);

  // 创建树面板按钮
  const treeButtons = createTreeButtons({
    handleCreate,
    handleRefresh: reloadTree,
  });

  // 详情面板相关逻辑
  const {
    activeTabKey,
    setActiveTabKey,
    detailButtons,
    detailTabs,
    renderTabContent,
  } = useSpaceDetailPanel({
    selectedSpace,
    treeData,
    setEditingSpace,
    setFormModalVisible,
    handleDetailPanelDelete,
  });

  return (
    <div style={{ height: "100%", padding: "0", overflow: "hidden" }}>
      <Row gutter={16} style={{ height: "100%" }}>
        {/* 左侧树形面板 */}
        <Col
          style={{
            width: "400px",
            flexShrink: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ContainerPanel
            title="空间树"
            buttons={treeButtons}
            height="100%"
            cardProps={{
              style: {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                height: "100%",
              },
            }}
          >
            <CompsTree
              treeData={treeData}
              loading={loading}
              onSelect={handleTreeSelect}
              onMenuClick={handleMenuClick}
              showSearch={true}
              showLine={true}
              showIcon={false}
              defaultExpandAll={false}
              menuItems={treeMenuItems}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              onExpand={handleNodeExpand}
            />
          </ContainerPanel>
        </Col>

        {/* 右侧详情面板 */}
        <Col style={{ flex: 1, height: "100%" }}>
          {selectedSpace ? (
            <ContainerPanel
              tabs={detailTabs}
              activeTabKey={activeTabKey}
              onTabChange={setActiveTabKey}
              buttons={detailButtons}
              height="100%"
              cardProps={{
                style: {
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                },
              }}
            >
              {renderTabContent()}
            </ContainerPanel>
          ) : (
            <ContainerPanel title="空间详情" height="100%">
              <div
                style={{
                  textAlign: "center",
                  color: "#999",
                  padding: "40px 0",
                }}
              >
                请选择一个空间查看详情
              </div>
            </ContainerPanel>
          )}
        </Col>
      </Row>

      {/* 表单弹窗 */}
      <ContainerModal
        visible={formModalVisible}
        title={getFormTitle(
          !!editingSpace?.id,
          !!editingSpace?.parentId && !editingSpace?.id
        )}
        width={FORM_WIDTH}
        height="auto"
        onCancel={() => setFormModalVisible(false)}
        footerRightButtons={[
          {
            key: "cancel",
            text: "取消",
            type: "default",
            onClick: () => setFormModalVisible(false),
          },
          {
            key: "submit",
            text: "确定",
            type: "primary",
            onClick: async () => {
              if (!formRef.current) return;

              try {
                const values = await formRef.current.validateFields();
                const success = await handleFormSuccess(values);
                if (success) {
                  setFormModalVisible(false);
                }
              } catch (error) {
                console.error("表单验证失败:", error);
              }
            },
          },
        ]}
      >
        <CompsForm
          formRef={(form) => {
            formRef.current = form;
          }}
          formItems={createSpaceFormItems(editingSpace)}
          initialValues={getDefaultFormValues(editingSpace)}
          layout={FORM_LAYOUT.layout}
          labelCol={FORM_LAYOUT.labelCol}
          wrapperCol={FORM_LAYOUT.wrapperCol}
        />
      </ContainerModal>
    </div>
  );
};

export default SpaceTree;
